/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  CellStatuses,
  CellValue,
  RoiFrame,
} from "@inscopix/ideas-hyperformula";
import { memo, useMemo } from "react";
import { CellValueRendererBase } from "../cell-value-renderers/CellValueRendererBase";
import { getValidationErrors } from "./CellRendererBase.helpers";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { EuiIcon } from "@inscopix/ideas-eui";
import { CellRendererFile } from "./CellRendererFile";
import { GridApi, IRowNode } from "ag-grid-community";
import { DataTableRowData, formatToolResources } from "../layout/DataTable";
import { CellRendererRoiFrame } from "./CellRendererRoiFrame";
import { CellRendererCellStatus } from "./CellRendererCellStatus";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { CellRendererMetadatumReference } from "./CellRendererMetadatumReference";
import { DATA_TABLE_TOOLTIP_ID } from "../layout/DataTableTooltip";
import { parseResourceTierCellValue } from "types/ResourceTiers";

/**
 * Formats cell values for display to the user based on the type of column where
 * the value is rendered.
 * @param value F
 * @param colDef
 * @returns The formatted cell value.
 */
const formatValue = (value: CellValue, colDef: DataTableColumnDefinition) => {
  // Display the value as the choice label in choice columns
  if (colDef.kind === "choice") {
    const choice = colDef.choices.find((choice) => choice.value === value);
    return choice?.label ?? value;
  } else if (colDef.kind === "resource_tier") {
    const parsedValue = parseResourceTierCellValue(value);
    if (!parsedValue.isResourceTierId) {
      return value;
    }

    return formatToolResources(
      {
        resources: colDef.default_resources,
        credits: colDef.default_resources.credits,
      },
      parsedValue.value,
    );
  } else {
    return value;
  }
};

const styles = {
  root: css`
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    display: flex;
    height: 100%;
    width: 100%;
    padding: 0px 11px;
    background-color: inherit;
    // Allow flex items to shrink
    & > * {
      min-width: 0;
    }
  `,
  error: css`
    background-color: #fff9e8;
  `,
  warningIcon: css`
    cursor: pointer;
  `,
};

interface CellRendererBaseProps {
  api: GridApi;
  eParentOfValue: HTMLElement;
  tableId: string;
  tableKind: "data" | "analysis" | "workflow";
  columnId: string;
  columnName: string;
  columnDefinition: DataTableColumnDefinition;
  isColumnRequired: boolean;
  rowId: string;
  rowNode: IRowNode<DataTableRowData>;
  formula: string;
  value: CellValue;
  isCustomFormula: boolean;
  isCellEditable: boolean;
  isRowExecuted: boolean;
  suppressFileSelection?: boolean;
  suppressFileContextMenu?: boolean;
  suppressFileDraggable?: boolean;
}

/**
 * Cell renderer that renders any cell value
 */
export const CellRendererBase = ({
  api,
  eParentOfValue,
  tableId,
  tableKind,
  columnId,
  columnName,
  columnDefinition,
  isColumnRequired,
  isCellEditable,
  rowId,
  rowNode,
  formula,
  value,
  isCustomFormula,
  isRowExecuted,
  suppressFileContextMenu,
  suppressFileSelection,
  suppressFileDraggable,
}: CellRendererBaseProps) => {
  const isEditingPermitted = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );

  const { validationErrors, rootStyles, tooltipContent } = useMemo(() => {
    // Hide validation errors on executed rows
    const validationErrors = !isRowExecuted
      ? getValidationErrors({
          tableKind,
          columnDefinition,
          isColumnRequired,
          value,
        })
      : [];

    // If there are multiple validation errors, render them in an unordered list
    const tooltipContent = (() => {
      if (validationErrors.length === 0) {
        return [];
      } else if (validationErrors.length === 1) {
        return [validationErrors[0].message];
      } else {
        return validationErrors.map((e) => `• ${e.message}`);
      }
    })();

    const rootStyles = [
      styles.root,
      ...(validationErrors.length > 0 ? [styles.error] : []),
    ];

    return { validationErrors, rootStyles, tooltipContent };
  }, [columnDefinition, isColumnRequired, isRowExecuted, tableKind, value]);

  return (
    <span css={rootStyles}>
      {(() => {
        // Render empty cells in file columns
        if (
          columnDefinition.kind === "file" &&
          isCellEditable &&
          isEditingPermitted
        ) {
          return (
            <CellRendererFile
              api={api}
              eParentOfValue={eParentOfValue}
              tableId={tableId}
              columnId={columnId}
              rowId={rowId}
              rowNode={rowNode}
              isCustomFormula={isCustomFormula}
              formula={formula}
              value={value}
              suppressFileContextMenu={suppressFileContextMenu}
              suppressFileSelection={suppressFileSelection}
              suppressFileDraggable={suppressFileDraggable}
            />
          );
        } else if (
          columnDefinition.kind === "cell_status" &&
          value instanceof CellStatuses
        ) {
          return (
            <CellRendererCellStatus
              api={api}
              columnId={columnId}
              rowId={rowId}
              rowNode={rowNode}
              value={value}
              isCellEditable={isCellEditable}
              isCustomFormula={isCustomFormula}
              columnName={columnName}
              columnDefinition={columnDefinition}
              tableId={tableId}
            />
          );
        } else if (
          columnDefinition.kind === "roi_frame" &&
          value instanceof RoiFrame
        ) {
          return (
            <CellRendererRoiFrame
              api={api}
              columnId={columnId}
              rowId={rowId}
              rowNode={rowNode}
              value={value}
              isCellEditable={isCellEditable}
              isCustomFormula={isCustomFormula}
              columnName={columnName}
              columnDefinition={columnDefinition}
              tableId={tableId}
            />
          );
        } else if (
          ["float", "integer", "text"].includes(columnDefinition.kind) &&
          tableKind === "analysis" &&
          isCellEditable &&
          isEditingPermitted
        ) {
          return (
            <CellRendererMetadatumReference
              eParentOfValue={eParentOfValue}
              tableId={tableId}
              columnId={columnId}
              rowId={rowId}
              formula={formula}
              value={value}
              isCustomFormula={isCustomFormula}
            />
          );
        } else if (columnDefinition.kind === "resource_tier") {
          return (
            <CellValueRendererBase
              value={formatValue(value, columnDefinition)}
              isDefaultValue={value === "" || value === null}
              address={{ tableId, columnId, rowId }}
              isDraggable={
                isCellEditable && isEditingPermitted && !suppressFileDraggable
              }
              suppressFileContextMenu={suppressFileContextMenu}
              suppressFileSelection={suppressFileSelection}
            />
          );
        } else {
          return (
            <CellValueRendererBase
              value={formatValue(value, columnDefinition)}
              isDefaultValue={!isCustomFormula}
              address={{ tableId, columnId, rowId }}
              isDraggable={
                isCellEditable && isEditingPermitted && !suppressFileDraggable
              }
              suppressFileContextMenu={suppressFileContextMenu}
              suppressFileSelection={suppressFileSelection}
            />
          );
        }
      })()}

      {validationErrors.length > 0 && (
        <EuiIcon
          css={styles.warningIcon}
          color="warning"
          data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
          data-tooltip-html={tooltipContent.join("<br />")}
          type="warning"
        />
      )}
    </span>
  );
};

export const CellRendererBaseMemo = memo(CellRendererBase);
